import { useState, useEffect, useMemo } from 'react';
import { TextField, Button, MenuItem, Box, Grid, Dialog, DialogTitle, DialogContent, DialogActions, Alert, Paper, Typography, Accordion, AccordionSummary, AccordionDetails, FormControlLabel, Checkbox, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalculateIcon from '@mui/icons-material/Calculate';
import { calculatePAC, calculatePOD } from '../utils/ingredientCalculations';

const initialState = {
  name: '',
  category: '',
  sugars: '',
  fats: '',
  milkfat: '',
  proteins: '',
  other_solids: '',
  total_solids: '',
  water: '',
  pac: '',
  pod: '',
  msnf: '',
  lactose: '',
  alcohol: '',
  salt: '',
  stabilizer: '',
  emulsifiers: '',
  hardening_factor: '',
  can_be_mix: true,
  can_be_inclusion: false
};

const cats = [
  { value: 'Sugar', label: 'Sugar' },
  { value: 'Alcohol', label: 'Alcohol' },
  { value: 'Nut', label: 'Nut' },
  { value: 'Flavour', label: 'Flavour' },
  { value: 'Fruit', label: 'Fruit' },
  { value: 'Stabilizer', label: 'Stabilizer' },
  { value: 'Vegetable', label: 'Vegetable' },
  { value: 'Chocolate', label: 'Chocolate' },
  { value: 'Oil', label: 'Oil' },
  { value: 'Dairy', label: 'Dairy' },
  { value: 'Egg', label: 'Egg' },
  { value: 'Emulsifier', label: 'Emulsifier' },
  { value: 'Other', label: 'Other' }
];

// Idiomas soportados
const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'it', label: 'Italiano' },
  { code: 'fr', label: 'Français' },
  { code: 'pt', label: 'Português' }
];

const IngredientForm = ({ onSubmit, ingredient, open, onClose, api }) => {
  const [form, setForm] = useState(initialState);
  const [translations, setTranslations] = useState({});
  const [editCompositionAnyway, setEditCompositionAnyway] = useState(false);
  const { t } = useTranslation();

  // Estado para calculadora PAC avanzada
  const [specificSugars, setSpecificSugars] = useState({
    sucrose: '',
    glucose: '',
    fructose: '',
    lactose: '',
    maltose: '',
    galactose: '',
    maltotriose: '',
    trehalose: ''
  });

  // Validaciones calculadas
  const validation = useMemo(() => {
    const sugars = parseFloat(form.sugars) || 0;
    const fats = parseFloat(form.fats) || 0;
    const proteins = parseFloat(form.proteins) || 0;
    const other_solids = parseFloat(form.other_solids) || 0;
    const salt = parseFloat(form.salt) || 0;
    const water = parseFloat(form.water) || 0;
    const total_solids = parseFloat(form.total_solids) || 0;
    const alcohol = parseFloat(form.alcohol) || 0;

    const calculatedTotalSolids = sugars + fats + proteins + other_solids + salt;
    const totalSolidsDiff = Math.abs(total_solids - calculatedTotalSolids);
    const totalSolidsValid = totalSolidsDiff < 0.01;

    const totalComposition = water + total_solids + alcohol;
    const totalDiff = Math.abs(100 - totalComposition);
    const compositionValid = totalDiff < 0.01;

    return {
      totalSolidsValid,
      totalSolidsDiff,
      calculatedTotalSolids,
      compositionValid,
      totalDiff,
      totalComposition
    };
  }, [form.sugars, form.fats, form.proteins, form.other_solids, form.salt, form.water, form.total_solids, form.alcohol]);

  // Estimación de PAC
  const pacEstimate = useMemo(() => {
    const sugars = parseFloat(form.sugars) || 0;
    const alcohol = parseFloat(form.alcohol) || 0;
    const salt = parseFloat(form.salt) || 0;
    
    const pac_min = (sugars * 0.5) + (alcohol * 7.4) + (salt * 5.85);
    const pac_max = (sugars * 1.9) + (alcohol * 7.4) + (salt * 5.85);
    
    return {
      min: pac_min.toFixed(2),
      max: pac_max.toFixed(2)
    };
  }, [form.sugars, form.alcohol, form.salt]);

  // Estimación de POD
  const podEstimate = useMemo(() => {
    const sugars = parseFloat(form.sugars) || 0;
    
    const pod_min = sugars * 0.16;  // Usando factor de lactosa (menor)
    const pod_max = sugars * 1.7;   // Usando factor de fructosa (mayor)
    
    return {
      min: pod_min.toFixed(2),
      max: pod_max.toFixed(2)
    };
  }, [form.sugars]);

  // Calculadora PAC y POD avanzada
  const calculatedValues = useMemo(() => {
    const sucrose = parseFloat(specificSugars.sucrose) || 0;
    const glucose = parseFloat(specificSugars.glucose) || 0;
    const fructose = parseFloat(specificSugars.fructose) || 0;
    const lactose = parseFloat(specificSugars.lactose) || 0;
    const maltose = parseFloat(specificSugars.maltose) || 0;
    const galactose = parseFloat(specificSugars.galactose) || 0;
    const maltotriose = parseFloat(specificSugars.maltotriose) || 0;
    const trehalose = parseFloat(specificSugars.trehalose) || 0;
    
    const totalSpecificSugars = sucrose + glucose + fructose + lactose + maltose + galactose + maltotriose + trehalose;
    const alcohol = parseFloat(form.alcohol) || 0;
    const salt = parseFloat(form.salt) || 0;
    
    // Usar helper para cálculos
    const ingredientData = {
      ...specificSugars,
      alcohol,
      salt
    };

    const pac = calculatePAC(ingredientData);
    const pod = calculatePOD(ingredientData);
    
    return {
      pac: pac.toFixed(2),
      pod: pod.toFixed(2),
      totalSpecificSugars: totalSpecificSugars.toFixed(2)
    };
  }, [specificSugars, form.alcohol, form.salt]);

  useEffect(() => {
    if (ingredient && ingredient.id) {
      // Edición normal: cargar desde API
      api.getIngredient(ingredient.id, { lang: 'all' })
        .then(data => {
          setForm({ ...initialState, ...data, category: data.category || '' });
          const loaded = {};
          if (data.translations) {
            Object.keys(data.translations).forEach(code => {
              loaded[code] = data.translations[code] || '';
            });
          } else {
            LANGS.forEach(l => {
              loaded[l.code] = data[`name_${l.code}`] || '';
            });
          }
          setTranslations(loaded);
        });
    } else if (ingredient && !ingredient.id) {
      const { id, ...rest } = ingredient;
      setForm({ ...initialState, ...rest, category: rest.category || '' });
      const loaded = {};
      if (rest.translations) {
        Object.keys(rest.translations).forEach(code => {
          loaded[code] = rest.translations[code] || '';
        });
      } else {
        LANGS.forEach(l => {
          loaded[l.code] = rest[`name_${l.code}`] || '';
        });
      }
      setTranslations(loaded);
    } else {
      setForm(initialState);
      const empty = {};
      LANGS.forEach(l => { empty[l.code] = ''; });
      setTranslations(empty);
    }
    // eslint-disable-next-line
  }, [ingredient, open, api]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleTranslationChange = (lang, value) => {
    setTranslations({ ...translations, [lang]: value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    
    // Solo validar composición si:
    // - Puede ir en mezcla (can_be_mix), O
    // - Es solo chunks pero se eligió editar composición de todas formas
    const isOnlyChunks = !form.can_be_mix && form.can_be_inclusion;
    const shouldValidateComposition = form.can_be_mix || (isOnlyChunks && editCompositionAnyway);
    
    // Advertencia si hay errores de validación y se debe validar
    if (shouldValidateComposition && (!validation.totalSolidsValid || !validation.compositionValid)) {
      const confirmMsg = t('composition_warning') || 
        'Las fórmulas de composición no coinciden. ¿Desea guardar de todos modos?';
      if (!window.confirm(confirmMsg)) {
        return;
      }
    }

    const payload = { ...form, translations };
    if (ingredient && ingredient.id) {
      await api.putIngredient(ingredient.id, payload);
    } else {
      await api.postIngredient(payload);
    }
    if (onSubmit) onSubmit();
    if (onClose) onClose();
  };

  const handleSpecificSugarChange = (name, value) => {
    setSpecificSugars({ ...specificSugars, [name]: value });
  };

  const applyCalculatedPAC = () => {
    setForm({ ...form, pac: calculatedValues.pac });
  };

  const applyCalculatedPOD = () => {
    setForm({ ...form, pod: calculatedValues.pod });
  };

  const applyBothCalculated = () => {
    setForm({ ...form, pac: calculatedValues.pac, pod: calculatedValues.pod });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {ingredient ? t('update') : t('add')} {t('ingredient')}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item size={12}>
              <TextField
                name="name"
                label={t('name') + ' (en)'}
                value={form.name}
                onChange={handleChange}
                required
                fullWidth
              />
            </Grid>
            {/* Campos para traducciones dinámicos */}
            {LANGS.filter(l => l.code !== 'en').map(lang => (
              <Grid item size={6} key={lang.code}>
                <TextField
                  label={t(`name.${lang.code}`)}
                  value={translations[lang.code] || ''}
                  onChange={e => handleTranslationChange(lang.code, e.target.value)}
                  fullWidth
                />
              </Grid>
            ))}
            <Grid item size={12}>
                <TextField
                  select
                  name="category"
                  label={t('category')}
                  value={form.category}
                  onChange={handleChange}
                  required
                  fullWidth
                  displayEmpty
                  sx={{ minWidth: "100%" }}
                >
                  {cats.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.value.toLowerCase())}
                    </MenuItem>
                  ))}
                </TextField>
            </Grid>

            {/* Toggle para tipo de uso del ingrediente */}
            <Grid item size={12}>
              <Box>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  {t('ingredient_usage_type') || 'Tipo de uso del ingrediente'}
                </Typography>
                <ToggleButtonGroup
                  value={
                    form.can_be_mix && form.can_be_inclusion ? 'both' :
                    form.can_be_inclusion ? 'inclusion' :
                    'mix'
                  }
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setForm({
                        ...form,
                        can_be_mix: newValue === 'mix' || newValue === 'both',
                        can_be_inclusion: newValue === 'inclusion' || newValue === 'both'
                      });
                    }
                  }}
                  fullWidth
                  sx={{ display: 'flex' }}
                >
                  <ToggleButton value="inclusion" sx={{ flex: 1 }}>
                    {t('only_inclusion') || 'Only for inclusion'}
                  </ToggleButton>
                  <ToggleButton value="mix" sx={{ flex: 1 }}>
                    {t('only_mix') || 'Only for mix'}
                  </ToggleButton>
                  <ToggleButton value="both" sx={{ flex: 1 }}>
                    {t('both_types') || 'Both'}
                  </ToggleButton>
                </ToggleButtonGroup>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t('ingredient_usage_type_help') || 'Define si el ingrediente puede usarse como trozo/variegado, en mezcla, o ambos'}
                </Typography>
              </Box>
            </Grid>

            {/* Checkbox para editar composición cuando es solo chunks */}
            {!form.can_be_mix && form.can_be_inclusion && (
              <Grid item size={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editCompositionAnyway}
                      onChange={(e) => setEditCompositionAnyway(e.target.checked)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {t('edit_composition_anyway') || 'Editar composición de todas formas'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('edit_composition_anyway_help') || 'Permite editar la composición química aunque el ingrediente sea solo para inclusión'}
                      </Typography>
                    </Box>
                  }
                />
              </Grid>
            )}

            {/* Mostrar campos de composición si puede ir en mezcla O si es solo chunks pero se eligió editar */}
            {(form.can_be_mix || editCompositionAnyway) && (
              <>
            {[
              ['sugars', t('sugars')],
              ['fats', t('fats')],
              ['milkfat', t('milkfat')],
              ['proteins', t('proteins')],
              ['other_solids', t('other_solids')],
              ['total_solids', t('total_solids')],
              ['water', t('water')],
              ['msnf', t('msnf')],
              ['lactose', t('lactose')],
              ['alcohol', t('alcohol')],
              ['salt', t('salt')],
              ['stabilizer', t('stabilizer')],
              ['emulsifiers', t('emulsifiers')],
              ['pac', t('pac')],
              ['pod', t('pod')],
              ['hardening_factor', t('hardening_factor')]
            ].map(([key, label]) => (
              <Grid item size={{ xs: 6, sm: 4, md:3 }} key={key}>
                <TextField
                  name={key}
                  label={label}
                  value={form[key]}
                  onChange={handleChange}
                  type="number"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: "any" }}
                />
              </Grid>
            ))}
            </>
            )}

            {/* Estimador de PAC - solo si puede ir en mezcla O si se eligió editar composición */}
            {(form.can_be_mix || editCompositionAnyway) && (
            <>
            <Grid item size={12}>
              <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalculateIcon fontSize="small" />
                  {t('pac_estimate') || 'Estimación de PAC'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('pac_range') || 'Rango estimado'}: <strong>{pacEstimate.min}</strong> - <strong>{pacEstimate.max}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t('pac_estimate_note') || 'Basado en azúcares totales, alcohol y sal. Use la calculadora avanzada para mayor precisión.'}
                </Typography>
              </Paper>
            </Grid>

            {/* Estimador de POD */}
            <Grid item size={12}>
              <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalculateIcon fontSize="small" />
                  {t('pod_estimate') || 'Estimación de POD'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('pod_range') || 'Rango estimado'}: <strong>{podEstimate.min}</strong> - <strong>{podEstimate.max}</strong>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t('pod_estimate_note') || 'Basado en azúcares totales. Use la calculadora avanzada para mayor precisión.'}
                </Typography>
              </Paper>
            </Grid>

            {/* Calculadora PAC/POD Avanzada */}
            <Grid item size={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalculateIcon fontSize="small" />
                    {t('pac_pod_calculator') || 'Calculadora PAC/POD Avanzada'}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item size={12}>
                      <Typography variant="caption" color="text.secondary">
                        {t('pac_pod_calculator_note') || 'Ingrese los azúcares específicos para un cálculo preciso de PAC y POD'}
                      </Typography>
                    </Grid>
                    
                    {/* Azúcares específicos */}
                    <Grid item size={6}>
                      <TextField
                        label={t('sucrose') || 'Sacarosa'}
                        type="number"
                        value={specificSugars.sucrose}
                        onChange={(e) => handleSpecificSugarChange('sucrose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 1.0 | POD: 1.0"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label={t('glucose') || 'Glucosa'}
                        type="number"
                        value={specificSugars.glucose}
                        onChange={(e) => handleSpecificSugarChange('glucose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 1.9 | POD: 0.75"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label={t('fructose') || 'Fructosa'}
                        type="number"
                        value={specificSugars.fructose}
                        onChange={(e) => handleSpecificSugarChange('fructose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 1.9 | POD: 1.7"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label={t('lactose')}
                        type="number"
                        value={specificSugars.lactose}
                        onChange={(e) => handleSpecificSugarChange('lactose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 1.0 | POD: 0.16"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label={t('maltose') || 'Maltosa'}
                        type="number"
                        value={specificSugars.maltose}
                        onChange={(e) => handleSpecificSugarChange('maltose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 1.0 | POD: 0.4"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label={t('galactose') || 'Galactosa'}
                        type="number"
                        value={specificSugars.galactose}
                        onChange={(e) => handleSpecificSugarChange('galactose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 1.9 | POD: 0.35"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label={t('maltotriose') || 'Maltotriosa'}
                        type="number"
                        value={specificSugars.maltotriose}
                        onChange={(e) => handleSpecificSugarChange('maltotriose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 0.65 | POD: 0.45"
                      />
                    </Grid>
                    <Grid item size={6}>
                      <TextField
                        label={t('trehalose') || 'Trehalosa'}
                        type="number"
                        value={specificSugars.trehalose}
                        onChange={(e) => handleSpecificSugarChange('trehalose', e.target.value)}
                        fullWidth
                        size="small"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: 0, step: "any" }}
                        helperText="PAC: 1.0 | POD: 0.45"
                      />
                    </Grid>

                    {/* Resultado calculado */}
                    <Grid item size={12}>
                      <Paper sx={{ p: 2, background: '#e3f2fd', mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Box>
                            <Typography variant="body2" gutterBottom>
                              <strong>{t('calculated_pac') || 'PAC Calculado'}:</strong> {calculatedValues.pac}
                            </Typography>
                            <Typography variant="body2" gutterBottom>
                              <strong>{t('calculated_pod') || 'POD Calculado'}:</strong> {calculatedValues.pod}
                            </Typography>
                          </Box>
                        </Box>
                        {calculatedValues.totalSpecificSugars > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('total_sugars') || 'Total de azúcares:'}: {calculatedValues.totalSpecificSugars}
                          </Typography>
                        )}
                        {form.alcohol > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('alcohol')}: {form.alcohol}%
                          </Typography>
                        )}
                        {form.salt > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('salt')}: {form.salt}%
                          </Typography>
                        )}
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={applyCalculatedPAC}
                          >
                            {t('apply_pac') || 'Aplicar PAC'}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={applyCalculatedPOD}
                          >
                            {t('apply_pod') || 'Aplicar POD'}
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            color="secondary"
                            onClick={applyBothCalculated}
                          >
                            {t('apply_both') || 'Aplicar Ambos'}
                          </Button>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
            </>
            )}

            {/* Alertas de validación - solo si puede ir en mezcla O si se eligió editar composición */}
            {(form.can_be_mix || editCompositionAnyway) && (
              <>
            {!validation.totalSolidsValid && (
              <Grid item size={12}>
                <Alert severity="warning">
                  <strong>{t('total_solids_mismatch') || 'Error en Sólidos Totales'}:</strong><br />
                  {t('total_solids')} = {t('sugars')} + {t('fats')} + {t('proteins')} + {t('other_solids')} + {t('salt')}<br />
                  {parseFloat(form.total_solids || 0).toFixed(2)} ≠ {validation.calculatedTotalSolids.toFixed(2)} 
                  {' '}({t('difference')}: {validation.totalSolidsDiff.toFixed(2)})
                </Alert>
              </Grid>
            )}

            {!validation.compositionValid && (
              <Grid item size={12}>
                <Alert severity="warning">
                  <strong>{t('composition_mismatch') || 'Error en Composición Total'}:</strong><br />
                  {t('water')} + {t('total_solids')} + {t('alcohol')} = 100<br />
                  {validation.totalComposition.toFixed(2)} ≠ 100.00 
                  {' '}({t('difference')}: {validation.totalDiff.toFixed(2)})
                </Alert>
              </Grid>
            )}

            {validation.totalSolidsValid && validation.compositionValid && (
              <Grid item size={12}>
                <Alert severity="success">
                  {t('composition_valid') || 'La composición es correcta'}
                </Alert>
              </Grid>
            )}
            </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button type="submit" variant="contained" color="primary">
            {ingredient ? t('update') : t('add')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default IngredientForm;